
import React from 'react';
import { useData } from '../../context/DataContext';
import { MessageSquare, Mail, Phone, Calendar, CheckCircle, Clock, Archive, Trash2 } from 'lucide-react';

const Inquiries: React.FC = () => {
  const { inquiries, updateInquiryStatus, deleteInquiry } = useData();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-800';
      case 'In Progress': return 'bg-yellow-100 text-yellow-800';
      case 'Closed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Inquiries & Leads</h1>
          <p className="text-gray-500">Manage contact requests, financing applications, and truck quotes.</p>
        </div>
      </div>

      <div className="bg-white rounded-sm shadow-sm border border-gray-100 overflow-hidden">
        {inquiries.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <MessageSquare size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">No inquiries received yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Client</th>
                  <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Message</th>
                  <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {inquiries.map(inquiry => (
                  <tr key={inquiry.id} className="hover:bg-gray-50 transition-colors">
                    <td className="p-4 whitespace-nowrap text-sm text-gray-600">
                      <div className="flex items-center">
                        <Calendar size={14} className="mr-2 text-gray-400" />
                        {new Date(inquiry.date).toLocaleDateString()}
                      </div>
                      <div className="flex items-center mt-1 text-xs text-gray-400">
                        <Clock size={12} className="mr-2" />
                        {new Date(inquiry.date).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="font-bold text-gray-900">{inquiry.name}</div>
                      <div className="flex items-center text-xs text-gray-500 mt-1">
                        <Mail size={12} className="mr-1" /> {inquiry.email}
                      </div>
                      <div className="flex items-center text-xs text-gray-500 mt-1">
                        <Phone size={12} className="mr-1" /> {inquiry.phone}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="px-2 py-1 bg-gray-100 rounded-sm text-xs font-bold uppercase">{inquiry.type}</span>
                    </td>
                    <td className="p-4">
                      <p className="text-sm text-gray-600 max-w-xs truncate">{inquiry.message}</p>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-sm text-xs font-bold uppercase ${getStatusColor(inquiry.status)}`}>
                        {inquiry.status}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end space-x-2">
                        {inquiry.status !== 'Closed' && (
                          <button
                            onClick={() => updateInquiryStatus(inquiry.id, 'Closed')}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                            title="Mark as Closed"
                          >
                            <CheckCircle size={18} />
                          </button>
                        )}
                        {inquiry.status === 'New' && (
                          <button
                            onClick={() => updateInquiryStatus(inquiry.id, 'In Progress')}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                            title="Mark In Progress"
                          >
                            <Archive size={18} />
                          </button>
                        )}
                        <a
                          href={`mailto:${inquiry.email}`}
                          className="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded"
                          title="Reply via Email"
                        >
                          <Mail size={18} />
                        </a>
                        <button
                          onClick={() => {
                            if (window.confirm(`Are you sure you want to delete this inquiry from ${inquiry.name}?`)) {
                              deleteInquiry(inquiry.id);
                            }
                          }}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          title="Delete Inquiry"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Inquiries;
